import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import ModalFrame from "../ui/primitives/ModalFrame";
import posthog from "posthog-js";

interface MayorStoriesProps {
  onVote: () => void;
  onRegister: () => void;
  onClose: () => void;
}

/**
 * Centered ModalFrame dialog (same primitive as other map popups).
 * One surface: night town-hall fills the panel; gold Island Event header is
 * the modal top edge (not a nested card); copy + CTAs sit on that scene.
 * No elder sprite.
 *
 * Sized for real mobile viewports (Telegram mini-app): no forced min-height,
 * no flex spacer that pushes CTAs off-screen; panel scrolls within ModalFrame
 * max-h when content is tall.
 */
export default function MayorStories({
  onVote,
  onRegister,
  onClose,
}: MayorStoriesProps) {
  const { t } = useTranslation();

  useEffect(() => {
    try {
      posthog.capture("election_announcement_shown");
    } catch {
      /* never block */
    }
  }, []);

  const handleVote = () => {
    try {
      posthog.capture("election_announcement_vote_clicked");
    } catch {
      /* never block */
    }
    onVote();
  };

  const handleRegister = () => {
    try {
      posthog.capture("election_announcement_register_clicked");
    } catch {
      /* never block */
    }
    onRegister();
  };

  const handleSkip = () => {
    try {
      posthog.capture("election_announcement_skipped", { step_reached: 0 });
    } catch {
      /* never block */
    }
    onClose();
  };

  return (
    <ModalFrame
      isOpen
      onClose={handleSkip}
      variant="dialog"
      title={t("Mayoral Election")}
      hideHeader
      closeOnBackdropClick={false}
      backdropClassName="z-[10000] items-center py-[max(12px,env(safe-area-inset-top))] pb-[max(12px,env(safe-area-inset-bottom))]"
      className="z-[10001] max-h-[min(85vh,calc(100dvh-24px))] max-w-[400px] overflow-y-auto border-none bg-election-story-frame-accent p-0 shadow-story-panel"
    >
      <div className="relative flex flex-col">
        <div className="pointer-events-none absolute inset-0 bg-[url('/game-assets/nscene-backgrounds/election-townhall.webp')] bg-cover bg-center bg-no-repeat" />
        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,var(--color-election-story-frame-accent)/25_0%,var(--color-election-story-frame-accent)/40_40%,var(--color-election-story-frame-accent)/78_100%)]" />

        <div className="relative z-[1] flex items-center gap-[10px] bg-[linear-gradient(135deg,var(--color-reward-medal-dark)_0%,var(--color-gold)_45%,var(--color-reward-medal)_100%)] px-2xl py-lg shadow-[0_4px_20px_var(--color-reward-alpha-30)]">
          <span className="shrink-0 text-[28px] leading-none" aria-hidden>
            🏛️
          </span>
          <div className="min-w-0 flex-1">
            <div className="[font-family:'Rubik_One',sans-serif] text-[13px] leading-[1.2] font-normal tracking-[0.08em] text-story-ink-alpha-70 uppercase">
              {t("Island Event")}
            </div>
            <div className="[font-family:'Rubik_One',sans-serif] text-[19px] leading-[1.25] font-normal tracking-[-0.01em] text-election-story-frame-text">
              {t("Mayoral Election")}
            </div>
          </div>
          <button
            type="button"
            autoFocus
            onClick={handleSkip}
            className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full border-none bg-black-alpha-25 text-[22px] leading-none text-election-story-frame-text"
            aria-label={t("Close")}
          >
            ×
          </button>
        </div>

        <div className="relative z-[1] px-2xl pt-xl">
          <div className="rounded-xl border border-solid border-white-alpha-10 bg-black-alpha-70 px-2xl py-lg backdrop-blur-[10px]">
            <p className="m-0 whitespace-pre-line [font-family:'Lato',sans-serif] text-[15px] leading-[24px] font-normal text-white [word-break:break-word]">
              {t("Hello, islander! Mayoral elections are live right now—this is your window to help choose who leads the island.")}
              {"\n\n"}
              {t("Every vote counts: back someone you trust, put your own name forward for mayor, or do both when you are ready.")}
            </p>
          </div>
        </div>

        {/* Small night-scene breath — fixed, not flex-grow (grow clipped CTAs on short phones) */}
        <div className="relative z-[1] h-[48px] shrink-0" aria-hidden />

        <div className="relative z-[1] flex flex-col gap-md px-2xl pt-md pb-2xl">
          <button
            type="button"
            onClick={handleVote}
            className="flex w-full cursor-pointer items-center justify-center gap-md rounded-[14px] border-none bg-[linear-gradient(135deg,var(--color-reward-medal-dark)_0%,var(--color-gold)_50%,var(--color-reward-medal)_100%)] px-3xl py-lg [font-family:'Rubik_One',sans-serif] text-[16px] font-normal tracking-[0.01em] text-election-story-frame-text shadow-[0_4px_20px_var(--color-reward-alpha-35)]"
          >
            <span className="text-[18px]" aria-hidden>
              🗳️
            </span>
            {t("I want to vote")}
          </button>
          <button
            type="button"
            onClick={handleRegister}
            className="flex w-full cursor-pointer items-center justify-center gap-md rounded-[14px] border border-solid border-reward-alpha-40 bg-black-alpha-45 px-3xl py-lg [font-family:'Rubik_One',sans-serif] text-[15px] font-normal tracking-[0.01em] text-gold shadow-[0_4px_16px_var(--color-black-alpha-30)] backdrop-blur-[8px]"
          >
            {t("I want to become the mayor")}
          </button>
        </div>
      </div>
    </ModalFrame>
  );
}
